import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Avatar,
    Typography,
    CircularProgress,
    Alert,
    Grid,
    Divider,
    Chip,
    Rating,
    Tooltip,
    Container,
    Paper,
    Skeleton,
    Stack,
    Button, // Import Button component
    useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom'; // Import useLocation

// --- DEPENDENCIES & HELPERS ---
import { API_BASE_URL } from '../../utils/constants';
import { useBitcoinPrice } from '../../state/context/bitcoin.context';
import { getStarsFromRating, transformToBTC } from '../../utils/helpers';

import UserBtcBalance from './UserBTCBalance.component';
import UserHeader from './UserHeader.component';


const truncateAddress = (address, length = 10) => {
    if (!address) return '';
    return `${ address.substring(0, length) }...${ address.substring(address.length - length) }`;
};
// --- NEW SKELETON COMPONENT ---
const UserScoreHeaderSkeleton = () => {
    const theme = useTheme();

    return (
        <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
            <Container maxWidth="md" sx={{ py: 2 }}>
                <Skeleton variant="rectangular" height={70} sx={{ mb: 2, borderRadius: 1 }} />
                <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2, mb: 2 }} />
                <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 2 }} />
            </Container>
        </Paper>
    );
};


// --- REFACTORED COMPONENTS ---
const getRatingChipProps = (rating, t) => {
    const map = {
        excellent: { color: 'success', label: t('user.excellent') },
        good: { color: 'info', label: t('user.good') },
        average: { color: 'warning', label: t('user.average') },
        low: { color: 'error', label: t('user.low') },
    };
    return map[rating?.toLowerCase()] || { color: 'default', label: rating || t('common.notAvailable') };
};

const InvestmentDisplay = ({ title, value, t }) => {
    const { price: btcPrice } = useBitcoinPrice();
    const theme = useTheme();
    const usd = value ? (btcPrice * (value / 1e11)) : 0;
    const color = title.includes('Profit')
        ? (value > 0 ? 'success.main' : value < 0 ? 'error.main' : 'text.primary')
        : 'secondary.main';

    return (
        <Typography variant="caption" color="text.secondary">
            {title}:{' '}
            {value ? (
                <>
                    <Box component="span" sx={{ fontWeight: 'bold', color }}>{transformToBTC(value)}</Box>{' '}
                    <Typography component="span" variant="caption">
                        / {usd.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                    </Typography>
                </>
            ) : (
                <Box component="span" sx={{ fontWeight: 'bold' }}>{t('common.notAvailable')}</Box>
            )}
        </Typography>
    );
};

const renderScoreSection = (title, info = {}, color = 'primary', t) => {
    const { score, rating, percentile, comparedAgainst, totalRealizedProfitBtc, totalInvestmentCostBtc, profitableTradeCount } = info;
    const isTrader = title === t('user.trader');
    const stars = getStarsFromRating(rating);
    const profitRatio = isTrader && totalInvestmentCostBtc > 1e-9
        ? ((totalRealizedProfitBtc / totalInvestmentCostBtc) * 100).toFixed(1)
        : null;
    const theme = useTheme();

    return (
        <Box sx={{
            p: 2,
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
            borderRadius: 2,
            border: `1px solid ${ theme.palette.divider }`
        }}>
            <Typography variant="subtitle1" color={`${ color }.main`} sx={{ fontWeight: 'bold' }}>
                {title}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {rating ? (
                    <>
                        <Chip size="small" variant="outlined" {...getRatingChipProps(rating, t)} />
                        <Rating value={stars} readOnly size="small" precision={0.5} />
                    </>
                ) : (
                    <Typography variant="caption" color="text.secondary">{t('user.noRating')}</Typography>
                )}
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {t('user.score')}: <Box component="span" sx={{ fontWeight: 'bold', color: 'text.primary' }}>{score?.toFixed(2) ?? t('common.notAvailable')}</Box>
            </Typography>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {t('user.percentile')}: <Box component="span" sx={{ fontWeight: 'bold', color: 'text.primary' }}>{percentile ?? t('common.notAvailable')}%</Box>{' '}
                <Typography component="span" variant="caption">
                    ({t('user.topPercent', { percent: 100 - (percentile ?? 0), total: comparedAgainst?.toLocaleString() ?? 0 })})
                </Typography>
            </Typography>

            {isTrader && (
                <Box mt={1.5} borderTop={1} borderColor="divider" pt={1.5}>
                    <Tooltip title={`Raw: ${ totalRealizedProfitBtc ?? t('common.notAvailable') } BTC`} placement="top">
                        <Box sx={{ mb: 0.5 }}>
                            <InvestmentDisplay title={t('user.realizedProfit')} value={totalRealizedProfitBtc} t={t} />
                        </Box>
                    </Tooltip>
                    <Tooltip title={`Raw: ${ totalInvestmentCostBtc ?? t('common.notAvailable') } BTC`} placement="top">
                        <Box sx={{ mb: 0.5 }}>
                            <InvestmentDisplay title={t('user.totalInvested')} value={totalInvestmentCostBtc} t={t} />
                        </Box>
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        {t('user.profitRatio')}: <Box component="span" sx={{ fontWeight: 'bold', color: 'text.primary' }}>{profitRatio ?? t('common.notAvailable')}%</Box>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {t('user.profitableSells')}:{' '}
                        <Box component="span" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                            {profitableTradeCount?.toLocaleString() ?? t('common.notAvailable')}
                        </Box>
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

function UserScoreHeader({ userId, isRow = false }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [scoreData, setScoreData] = useState(null);
    const [error, setError] = useState('');
    const [user, setUser] = useState(null);
    const navigate = useNavigate();
    const isUserPage = typeof window !== 'undefined' && window.location.href.includes('/users/');
    const theme = useTheme();
    const { pathname } = useLocation(); // Use useLocation hook
    const [isCollapsed, setIsCollapsed] = useState(pathname.includes('tokens')); // State for collapse



    useEffect(() => {
        fetch(`https://api.odin.fun/v1/user/${ userId }`)
            .then(res => res.ok ? res.json() : Promise.reject('Failed to fetch user data'))
            .then(setUser)
            .catch(console.error);
    }, [userId]);

    const fetchScores = useCallback(async () => {
        if (!userId) return;
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${ API_BASE_URL }/users/${ userId }/scores`);
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || `Request failed: ${ res.status }`);
            setScoreData(data);
        } catch (err) {
            setError(err.message || t('user.couldNotLoadScores'));
        } finally {
            setLoading(false);
        }
    }, [userId, t]);

    useEffect(() => {
        fetchScores();
    }, [fetchScores]);

    if (loading) return <UserScoreHeaderSkeleton />;

    // Collapsed view: only header and creator score
    if (isCollapsed) {
        return (
            <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
                <UserHeader userId={userId} />
                <Container maxWidth="md" sx={{ py: 2 }}>
                    {error && <Alert severity="warning" variant="outlined">{error}</Alert>}
                    {scoreData && !error && (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>{renderScoreSection(t('user.creator'), scoreData.creatorScore, 'primary', t)}</Grid>
                        </Grid>
                    )}
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <Button variant="outlined" onClick={() => setIsCollapsed(false)}>
                            {t('user.seeMoreDetails')}
                        </Button>
                    </Box>
                </Container>
            </Paper>
        );
    }

    // Full view (when not collapsed or when isRow is true)
    if (isRow) {
        return (
            <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden', mb: 3, display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', p: 2, borderBottom: `1px solid ${ theme.palette.divider }` }}>
                    <Avatar src={`https://images.odin.fun/user/${ userId }`} sx={{ width: 64, height: 64, mr: 2, border: `2px solid ${ theme.palette.secondary.main }` }}>
                        {(user?.username || 'U').slice(0, 2).toUpperCase()}
                    </Avatar>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>{user?.username || t('user.userName')}</Typography>
                        <Typography variant="caption" sx={{ cursor: 'pointer', opacity: 0.8, '&:hover': { textDecoration: 'underline', opacity: 1 } }}
                            onClick={() => isUserPage ? window.open(`https://odin.fun/user/${ userId }`, '_blank') : window.open(`/users/${ userId }`)}
                        >
                            {truncateAddress(userId)}
                        </Typography>
                    </Box>
                </Box>

                <Container maxWidth="md" sx={{ py: 2, display: 'flex', flexDirection: 'row', gap: 2 }}>
                    <UserBtcBalance userId={userId} />
                    {error && <Alert severity="warning" variant="outlined">{error}</Alert>}
                    {scoreData && !error && (
                        <>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>{renderScoreSection(t('user.creator'), scoreData.creatorScore, 'primary', t)}</Grid>
                                <Grid item xs={6}>{renderScoreSection(t('user.trader'), scoreData.traderScore, 'secondary', t)}</Grid>
                            </Grid>
                        </>
                    )}
                    {!loading && !error && !scoreData && (
                        <Typography variant="caption" color="text.secondary" textAlign="center" py={2}>
                            {userId ? t('user.noScoreDataAvailable') : t('user.userIdNotProvided')}
                        </Typography>
                    )}
                </Container>
            </Paper>
        );
    }

    return (
        <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
            <UserHeader userId={userId} isCollapsed={true} />

            <Container maxWidth="md" sx={{ py: 2 }}>
                <UserBtcBalance userId={userId} />
                {error && <Alert severity="warning" variant="outlined">{error}</Alert>}
                {scoreData && !error && (
                    <>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>{renderScoreSection(t('user.creator'), scoreData.creatorScore, 'primary', t)}</Grid>
                            <Grid item xs={12}>{renderScoreSection(t('user.trader'), scoreData.traderScore, 'secondary', t)}</Grid>
                        </Grid>
                    </>
                )}
                {!loading && !error && !scoreData && (
                    <Typography variant="caption" color="text.secondary" textAlign="center" py={2}>
                        {userId ? t('user.noScoreDataAvailable') : t('user.userIdNotProvided')}
                    </Typography>
                )}
            </Container>
        </Paper>
    );
}

UserScoreHeader.propTypes = {
    userId: PropTypes.string.isRequired,
};

export default UserScoreHeader;
